import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  message,
} from 'antd';
import {
  AppstoreOutlined,
  EditOutlined,
  LoginOutlined,
  PlusOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useConnection } from '@sezenta/connection';
import {
  Contract,
  MODULE_LABELS,
  displayContractTypeName,
  getRefreshTokenCookie,
  isModuleAvailable,
  setLastContractId,
} from '../../context/ContractHelpers';
import { AdminLink } from './adminUi';

interface ContractType {
  id: number;
  name: string;
  // The master-data `by-type` endpoint flattens `extra` into the top level
  // (fromPgMasterData), so moduleKey arrives here — keep extra as a fallback.
  moduleKey?: string;
  extra?: { moduleKey?: string };
}

const moduleKeyOfType = (t?: ContractType): string | undefined =>
  t?.moduleKey ?? t?.extra?.moduleKey;

// New contract ids are UUID v7, whose first 48 bits encode the Unix-ms creation
// time — so we can show "created" without a DB column. Legacy/non-v7 ids (e.g.
// the default contract '1') return undefined and render as "—".
const createdFromId = (businessId?: string): number | undefined => {
  if (!businessId) return undefined;
  const hex = businessId.replace(/-/g, '');
  if (hex.length !== 32 || hex[12] !== '7') return undefined;
  const ms = parseInt(hex.slice(0, 12), 16);
  return Number.isFinite(ms) && ms > 0 ? ms : undefined;
};
const fmtCreated = (businessId?: string): string => {
  const ms = createdFromId(businessId);
  return ms ? dayjs(ms).format('DD MMM YYYY') : '—';
};

// Small UI bits — keep the admin surfaces from looking like raw antd CRUD.
const StatusPill: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[12px] font-medium ${
      active ? 'bg-[#E6F4F4] text-[#1A8B8F]' : 'bg-[#FDECEC] text-[#C0392B]'
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        active ? 'bg-[#1A8B8F]' : 'bg-[#C0392B]'
      }`}
    />
    {active ? 'Active' : 'Inactive'}
  </span>
);

const ModuleBadge: React.FC<{ moduleKey?: string }> = ({ moduleKey }) => {
  if (!moduleKey) return <span className="text-[#84919A]">—</span>;
  const bin = moduleKey === 'bin-management';
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[12px] font-medium ${
        bin ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'bg-[#FFF4E6] text-[#B7791F]'
      }`}
    >
      {MODULE_LABELS[moduleKey] ?? moduleKey}
    </span>
  );
};

const StatCard: React.FC<{ label: string; value: number | string }> = ({
  label,
  value,
}) => (
  <div
    className="bg-white rounded-[10px] px-5 py-4 border border-[#EAECF0] flex-1"
    style={{ boxShadow: '0px 1px 2px rgba(16,24,40,0.05)' }}
  >
    <div className="text-[13px] text-[#84919A]">{label}</div>
    <div className="text-[26px] font-semibold text-[#18191C] leading-tight">
      {value}
    </div>
  </div>
);

const Contracts: React.FC = () => {
  const connection = useConnection();
  const navigate = useNavigate();
  const [cookies] = useCookies(['refreshToken']);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [types, setTypes] = useState<ContractType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<Contract | null>(null);
  const [viewing, setViewing] = useState<Contract | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [form] = Form.useForm();

  // Server-side pagination + DB-computed stats (never derived from the page).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<{
    total: number;
    active: number;
    modules: Record<string, number>;
  }>({ total: 0, active: 0, modules: {} });

  const typeById = useMemo(
    () => Object.fromEntries(types.map((t) => [t.id, t])),
    [types],
  );
  const moduleKeyOf = (contractType?: number) =>
    contractType != null ? moduleKeyOfType(typeById[contractType]) : undefined;

  // Project types are a small list — fetched once for the per-row module label
  // and the create/edit drawer.
  useEffect(() => {
    connection
      .get('master-data/by-type/ContractType')
      .then((r) => setTypes(r.data?.data ?? []))
      .catch(() => undefined);
  }, [connection]);

  const loadContracts = useCallback(() => {
    setLoading(true);
    connection
      .get('contracts', {
        params: { page, limit: pageSize, includeInactive: 'true' },
      })
      .then((r) => {
        setContracts(r.data?.data ?? []);
        setTotal(r.data?.total ?? 0);
      })
      .catch(() => message.error('Failed to load projects'))
      .finally(() => setLoading(false));
  }, [connection, page, pageSize]);

  const loadStats = useCallback(() => {
    connection
      .get('contracts/stats')
      .then((r) => setStats(r.data))
      .catch(() => undefined);
  }, [connection]);

  useEffect(() => loadContracts(), [loadContracts]);
  useEffect(() => loadStats(), [loadStats]);

  const reload = useCallback(() => {
    loadContracts();
    loadStats();
  }, [loadContracts, loadStats]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: true });
    setOpen(true);
  };

  const openEdit = (record: Contract) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      contractType: record.contractType,
      status: record.status ?? true,
    });
    setOpen(true);
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // inline validation errors are shown by antd
    }
    setSubmitting(true);
    try {
      if (editing) {
        await connection.patch(`contracts/${editing.businessId}`, values);
        message.success('Project updated');
      } else {
        await connection.post('contracts', values);
        message.success('Project created');
      }
      setOpen(false);
      reload();
    } catch (e) {
      message.error('Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (record: Contract) => {
    try {
      await connection.delete(`contracts/${record.businessId}`);
      message.success('Project deactivated');
      reload();
    } catch (e) {
      message.error('Delete failed');
    }
  };

  // CR004: open a contract as a user would see it (scoped god-mode token).
  const openContract = async (record: Contract) => {
    // Guard: projects on a not-yet-built module (e.g. Bin Management) can't be
    // opened. The button is already disabled; this backs it up.
    if (!isModuleAvailable(moduleKeyOf(record.contractType))) {
      message.info(
        'This project’s module is coming soon and can’t be opened yet.',
      );
      return;
    }
    setOpeningId(record.businessId);
    try {
      const refresh = getRefreshTokenCookie() ?? cookies.refreshToken;
      if (!refresh) {
        setOpeningId(null);
        message.error('Your session expired — please log in again');
        return;
      }
      const sw = await connection.post(
        `auth/switch-contract/${record.businessId}`,
      );
      // Adopt the new contract-scoped refresh token (useUser/permissions derive
      // from it); fall back to the existing one if the backend is pre-dual-token.
      connection.login(sw.data.accessToken, sw.data.refreshToken ?? refresh);
      setLastContractId(record.businessId);
      navigate(`/portal/${record.businessId}/dashboard`);
    } catch (e: any) {
      setOpeningId(null);
      // Surface the server's reason (e.g. "Contract access denied") instead of a
      // generic message, so failures are diagnosable.
      const reason =
        e?.response?.data?.message ??
        e?.response?.data?.error ??
        e?.message ??
        'Could not open project';
      message.error(`Could not open project: ${reason}`);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_: any, r: Contract) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#E6F4F4] text-[#26A3A7] flex items-center justify-center text-[14px] font-semibold">
            {(r.name ?? '?').trim().charAt(0).toUpperCase()}
          </div>
          <span className="font-medium text-[#18191C]">
            {r.name ?? r.businessId}
          </span>
        </div>
      ),
    },
    {
      title: 'Project Type',
      key: 'contractType',
      render: (_: any, r: Contract) =>
        r.contractType != null
          ? displayContractTypeName(typeById[r.contractType]?.name) || '—'
          : '—',
    },
    {
      title: 'Module',
      key: 'module',
      render: (_: any, r: Contract) => (
        <ModuleBadge moduleKey={moduleKeyOf(r.contractType)} />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: any, r: Contract) => (
        <StatusPill active={r.status !== false} />
      ),
    },
    {
      title: 'Created',
      key: 'createdAt',
      width: 130,
      render: (_: any, r: Contract) => (
        <span className="text-[#5E6870]">{fmtCreated(r.businessId)}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      align: 'center' as const,
      render: (_: any, r: Contract) => {
        const available = isModuleAvailable(moduleKeyOf(r.contractType));
        return (
          <Space size="middle" onClick={(e) => e.stopPropagation()}>
            <Tooltip
              title={
                available
                  ? `Enter ${r.name ?? 'this project'}'s portal and operate inside it as this project`
                  : "This project's module isn't available yet — coming soon"
              }
              placement="bottom"
            >
              {/* span wrapper so the tooltip still shows on the disabled button */}
              <span>
                <Button
                  type="primary"
                  icon={<LoginOutlined />}
                  loading={openingId === r.businessId}
                  disabled={!available}
                  onClick={() => openContract(r)}
                  style={{ background: available ? '#26A3A7' : undefined }}
                >
                  Open portal
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Edit project" placement="bottom">
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => openEdit(r)}
                style={{ color: '#26A3A7', borderColor: '#26A3A7' }}
              />
            </Tooltip>
            <Popconfirm
              title="Deactivate this project?"
              okText="Deactivate"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove(r)}
            >
              <Tooltip title="Deactivate project" placement="bottom">
                <Button shape="circle" danger icon={<StopOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="w-full h-full p-6 overflow-auto bg-[#F7F9FB]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-4">
        <div>
          <h2 className="text-[22px] font-semibold text-[#18191C] m-0">
            Manage Projects
          </h2>
          <p className="text-[#84919A] text-[14px] m-0">
            Create projects and open any one to operate inside it.
          </p>
        </div>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={openCreate}
          style={{ background: '#26A3A7' }}
          className="w-fit"
        >
          New Project
        </Button>
      </div>

      <div className="flex gap-4 mb-6 flex-wrap">
        <StatCard label="Total projects" value={stats.total} />
        <StatCard label="Active" value={stats.active} />
        <StatCard
          label="Store Collection"
          value={stats.modules['bin-bag-management'] ?? 0}
        />
        <StatCard
          label="Bin Management"
          value={stats.modules['bin-management'] ?? 0}
        />
      </div>

      <div
        className="bg-white rounded-[10px] border border-[#EAECF0] p-2"
        style={{ boxShadow: '0px 1px 2px rgba(16,24,40,0.05)' }}
      >
        <Table
          rowKey="businessId"
          loading={loading}
          dataSource={contracts}
          columns={columns}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => setViewing(record),
            style: { cursor: 'pointer' },
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `${t} projects`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </div>

      {/* View project details */}
      <Drawer
        open={!!viewing}
        onClose={() => setViewing(null)}
        width={480}
        title="Project details"
        extra={
          viewing && (
            <Space>
              <Tooltip title="Edit project" placement="bottom">
                <Button
                  shape="circle"
                  icon={<EditOutlined />}
                  onClick={() => {
                    const v = viewing;
                    setViewing(null);
                    openEdit(v);
                  }}
                  style={{ color: '#26A3A7', borderColor: '#26A3A7' }}
                />
              </Tooltip>
              <Tooltip
                title={
                  isModuleAvailable(moduleKeyOf(viewing.contractType))
                    ? undefined
                    : "This project's module isn't available yet — coming soon"
                }
              >
                <span>
                  <Button
                    type="primary"
                    icon={<LoginOutlined />}
                    loading={openingId === viewing.businessId}
                    disabled={
                      !isModuleAvailable(moduleKeyOf(viewing.contractType))
                    }
                    onClick={() => openContract(viewing)}
                    style={{
                      background: isModuleAvailable(
                        moduleKeyOf(viewing.contractType),
                      )
                        ? '#26A3A7'
                        : undefined,
                    }}
                  >
                    Open portal
                  </Button>
                </span>
              </Tooltip>
            </Space>
          )
        }
      >
        {viewing && (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 rounded-xl bg-[#E6F4F4] text-[#26A3A7] flex items-center justify-center text-[24px] font-semibold">
                {(viewing.name ?? '?').trim().charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-[18px] font-semibold text-[#18191C]">
                  {viewing.name ?? viewing.businessId}
                </div>
                <StatusPill active={viewing.status !== false} />
              </div>
            </div>

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Project name">
                {viewing.name ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Project type">
                {viewing.contractType != null
                  ? displayContractTypeName(
                      typeById[viewing.contractType]?.name,
                    ) || '—'
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Module">
                <ModuleBadge moduleKey={moduleKeyOf(viewing.contractType)} />
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {viewing.status !== false ? 'Active' : 'Inactive'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {fmtCreated(viewing.businessId)}
              </Descriptions.Item>
              <Descriptions.Item label="Project ID">
                <span className="text-[12px] text-[#84919A] break-all">
                  {viewing.businessId}
                </span>
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Drawer>

      <Drawer
        title={editing ? 'Edit Project' : 'New Project'}
        open={open}
        onClose={() => setOpen(false)}
        width={440}
        extra={
          <Button
            type="primary"
            loading={submitting}
            onClick={submit}
            style={{ background: '#26A3A7' }}
          >
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Project name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="e.g. Southern Cross — VIC" size="large" />
          </Form.Item>
          <Form.Item
            name="contractType"
            label="Project type"
            tooltip="The project type fixes the single module this project runs."
            rules={[{ required: true, message: 'Project type is required' }]}
          >
            <Select
              size="large"
              placeholder="Select project type"
              optionLabelProp="label"
              notFoundContent={
                <div className="py-3 px-3 text-center">
                  <div className="text-[13px] font-medium text-[#5E6870] mb-1">
                    No project types configured
                  </div>
                  <div className="text-[12px] text-[#84919A] leading-snug">
                    Add them in{' '}
                    <AdminLink to="/admin/master-data">
                      Cross-Project Master Data → Project Types
                    </AdminLink>
                    .
                  </div>
                </div>
              }
              options={types.map((t) => {
                // Types whose module isn't built yet (e.g. Bin Management) are
                // shown but disabled, so no new project can be created on them.
                const available = isModuleAvailable(moduleKeyOfType(t));
                const base = displayContractTypeName(t.name);
                return {
                  value: t.id,
                  label: available ? base : `${base} (coming soon)`,
                  title: available ? base : `${base} — module coming soon`,
                  disabled: !available,
                };
              })}
            />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const sel = form.getFieldValue('contractType');
              const mk =
                sel != null ? moduleKeyOfType(typeById[sel]) : undefined;
              return (
                <div className="mb-4 -mt-2 flex items-center gap-2 text-[13px] text-[#5E6870]">
                  <AppstoreOutlined /> Module: <ModuleBadge moduleKey={mk} />
                </div>
              );
            }}
          </Form.Item>
          {editing && (
            <Form.Item name="status" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Drawer>
    </div>
  );
};

export default Contracts;
